/**
 * AppDetailPage — one deployed managed-app instance, tabbed surface.
 *
 * Mounted at /projects/:projectId/apps/:instanceId as a child of
 * ProjectLayout. Header chrome (title + status pill + meta + actions)
 * matches PipelineDetailPage so the two read as siblings. Below the
 * header is a tab strip — same `.pipeline-detail-tabs/.pipeline-detail-tab`
 * styling, same `?tab=…` URL convention as PipelineDetailPage, so
 * back/forward + share-the-link both work.
 *
 *   Overview       — horizontal meta row (URL / instance id / running /
 *                    resources / storage / hourly cost) + all the
 *                    config sections below it (bootstrap script, env
 *                    vars table, agent-ops scheduler, optional setup
 *                    hint). Mirrors PipelineDetailPage's `pipeline-stat-grid`.
 *   Logs           — 3s-poll tail of agent-ops logs (unchanged from v1)
 *   Conversations  — "Agent activity": 10s-poll list of structured run
 *                    records from GET /apps/:id/runs (the control-plane
 *                    app-runs table the agent-ops daemon pushes to).
 *                    Each row expands to lazy-fetch the full record
 *                    (prompts + result) via GET /apps/:id/runs/:runId.
 *
 * Default tab when `?tab` is missing or unknown: overview. Legacy
 * `?tab=config` links redirect to `?tab=overview` on mount so any
 * bookmarked / shared Config-tab links from the previous shape don't
 * land on an empty body.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  arrowBackOutline,
  copyOutline,
  openOutline,
  trashOutline,
  pauseOutline,
  playOutline,
  sparklesOutline,
  cloudUploadOutline,
  closeOutline,
  globeOutline,
} from 'ionicons/icons';
import { IonIcon, IonButton, IonSpinner } from '@ionic/react';
import cronstrue from 'cronstrue';
import { Apps } from '../../services/apps';
import { timeAgo } from '../../utils/timeAgo';
import AppStatus from './AppStatus';
import { resolveTier } from '../../components/billing/AppPricingBadge';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import DataTable from '../../components/shared/DataTable/DataTable';
import Credits, { formatRate, formatBalance } from '../../services/credits';
import InsufficientCreditsModal from '../../components/billing/InsufficientCreditsModal';
import MktAlertModal from '../../components/molecules/MktAlertModal/MktAlertModal';
import DangerButton from '../../components/shared/DangerButton/DangerButton';
import DangerZone from '../../components/shared/DangerZone/DangerZone';
// Reuse the pipeline detail stylesheet for the whole shell (header,
// tab strip, tab body, skeletons, stat grid). Apps marketplace
// stylesheet is still imported for legacy helper classes (e.g.
// `.apps-modal-helper`) used by the section bodies below the meta
// row. Local additions live in AppDetailPage.css.
import '../PipelinesPage/PipelineDetailPage.css';
import '../AppsMarketplacePage/AppsMarketplacePage.css';
import './AppDetailPage.css';

const TABS = [
  { id: 'overview',      label: 'Overview' },
  { id: 'logs',          label: 'Instance log' },
  { id: 'conversations', label: 'Agent activity' },
  { id: 'settings',      label: 'Settings' },
  { id: 'env',           label: 'ENV' },
];

const AppDetailPage = () => {
  const { projectId, instanceId, slug } = useParams();
  // Solo (dedicated-EC2) apps mount at /apps/solo/:slug and are keyed by
  // appSlug — they're NOT in the cloud instanceId-keyed table, so the whole
  // page takes a separate load path (`getSoloStatus`) and a slimmed-down
  // body. Cloud apps (`:instanceId`) are completely unaffected.
  const isSolo = !!slug;
  if (isSolo) return <SoloAppDetailPage slug={slug} projectId={projectId} />;
  return <CloudAppDetailPage projectId={projectId} instanceId={instanceId} />;
};

const CloudAppDetailPage = ({ projectId, instanceId }) => {
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Tab state — read from `?tab=…`, fall back to 'overview' on missing
  // / unknown. Mirrors PipelineDetailPage's pattern so users can deep-
  // link any tab.
  const tabFromUrl = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'overview';
  const setActiveTab = (id) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  // Legacy `?tab=config` links → redirect to Overview (Config content
  // got merged into the Overview tab). Replace history so the user
  // can't hit "back" into a dead tab id.
  useEffect(() => {
    if (tabFromUrl === 'config') {
      setSearchParams({ tab: 'overview' }, { replace: true });
    }
  }, [tabFromUrl, setSearchParams]);

  const fetchApp = useCallback(async () => {
    setLoading(true);
    try {
      const data = await Apps.getInstance(instanceId);
      setApp(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch app:', err);
      setError(err);
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { fetchApp(); }, [fetchApp]);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setToast?.({ isOpen: true, message: `Copied ${label}`, color: 'success' });
    });
  };

  // In-place agent-ops upgrade — re-registers the per-instance task
  // def with the latest image tag and force-redeploys the service.
  // EFS access point + bridgeToken + ALB wiring all preserved by the
  // backend's `upgradeApp` handler (see backend/src/handlers/apps.js).
  // Modal closes on success and we refetch the app to clear the pill.
  const handleUpdate = async () => {
    if (!app || updating) return;
    setUpdating(true);
    try {
      await Apps.upgrade(app.instanceId, { version: app.latestAgentOpsVersion });
      setToast?.({ isOpen: true, message: 'Upgrade started — task will restart in ~1 min', color: 'success' });
      setUpdateModalOpen(false);
      // Refetch so the pill disappears once the new TD is registered.
      // The actual rolling restart takes longer — but the row's
      // agentOpsVersion flips as soon as the new TD is the active one.
      fetchApp();
    } catch (err) {
      setToast?.({ isOpen: true, message: `Upgrade failed: ${err.message}`, color: 'danger' });
    } finally {
      setUpdating(false);
    }
  };

  // Destroy lives in the Settings tab's Danger zone now (consistency with
  // pipelines). The DangerZone owns the styled confirm modal + busy state;
  // this is its onConfirm. Cloud apps are Fargate — stopping a task is far
  // less destructive than the solo EBS+S3 data delete, so the DangerZone
  // uses the plain styled confirm variant (no type-to-confirm). Re-throws
  // on failure so the modal stays open for a retry.
  const destroyApp = async () => {
    if (!app) return;
    try {
      await Apps.destroy(app.instanceId);
      setToast?.({ isOpen: true, message: 'App destroyed', color: 'success' });
      navigate(`/projects/${projectId}/apps`);
    } catch (err) {
      setToast?.({ isOpen: true, message: `Destroy failed: ${err.message}`, color: 'danger' });
      throw err;
    }
  };

  // Loading state — same skeleton shape PipelineDetailPage renders, so
  // the visual continuity holds when the user clicks from the apps
  // list into a row.
  if (loading) {
    return (
      <div className="pipeline-detail-page" aria-busy="true" aria-label="Loading app">
        <div className="pipeline-detail-skel-header">
          <div className="pipeline-detail-skel pipeline-detail-skel--title" />
          <div className="pipeline-detail-skel pipeline-detail-skel--meta" />
        </div>
        <div className="pipeline-detail-skel-body">
          <div className="pipeline-detail-skel pipeline-detail-skel--row" />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '85%' }} />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <NotFoundPage
        code="404"
        title="App not found"
        subtitle="This app instance may have been destroyed, or you don't have access to it."
        primaryAction={{
          label: 'Back to apps',
          to: `/projects/${projectId}/apps`,
          icon: arrowBackOutline,
        }}
      />
    );
  }

  const publicUrl = app.url || (app.subdomain ? `https://${app.subdomain}` : null);

  return (
    <>
      <Helmet>
        <title>{`${app.name || app.appType || instanceId} · Apps · Zibby`}</title>
      </Helmet>

      <div className="pipeline-detail-page">
        {/* Header: title + meta row + actions. Same layout as the
            pipeline detail page so they read as siblings. */}
        <div className="pipeline-detail-header">
          <div className="pipeline-detail-titlerow">
            <h1 className="pipeline-detail-title">
              {app.displayName || app.name || app.appType || instanceId}
            </h1>
            <span className="pipeline-detail-meta-sep">·</span>
            <AppStatus
              status={app.status}
              runningCount={app.runningCount}
              desiredCount={app.desiredCount}
              variant="header"
            />
            <span className="pipeline-detail-meta-sep">·</span>
            <span className="pipeline-detail-version">
              {app.appType}{app.appVersion ? ` v${app.appVersion}` : ''}
            </span>
            {/* agent-ops version is meaningful even when the Update
                pill isn't showing — users want to know what version
                they're on, not just "you're up to date". */}
            {app.agentOpsVersion && (
              <>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-version" title="agent-ops sidecar version">
                  agent-ops v{app.agentOpsVersion}
                </span>
              </>
            )}
            <span className="pipeline-detail-meta-sep">·</span>
            <span className="pipeline-detail-time">
              Created {app.createdAt ? timeAgo(app.createdAt) : '—'}
            </span>
          </div>
          <div className="pipeline-detail-actions">
            {/* "Update available" pill — mirrors PipelineDetailPage's
                marketplace-update affordance so apps + workflows share
                the same App-Store-style language. Renders only when
                the backend flags the live agent-ops image tag as older
                than LATEST_AGENT_OPS_VERSION. Sits LEFT of the
                primary actions (Fitts' law). */}
            {app.agentOpsUpdateAvailable && (
              <button
                type="button"
                className="pipeline-detail-update-pill"
                onClick={() => setUpdateModalOpen(true)}
                title={`Update available — ${app.agentOpsVersion || '?'} → ${app.latestAgentOpsVersion || '?'}`}
              >
                <IonIcon icon={sparklesOutline} aria-hidden="true" />
                Update available
                {app.latestAgentOpsVersion && (
                  <span className="pipeline-detail-update-pill-ver">
                    v{app.latestAgentOpsVersion}
                  </span>
                )}
              </button>
            )}
            {publicUrl && (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="header-docs-link"
              >
                <IonIcon icon={openOutline} />
                <span>Open</span>
              </a>
            )}
            {/* Destroy moved into the Settings tab's Danger zone (parity
                with pipelines). */}
          </div>
        </div>

        {updateModalOpen && (
          <div
            className="pipeline-update-overlay"
            onClick={() => !updating && setUpdateModalOpen(false)}
            role="presentation"
          >
            <div
              className="pipeline-update-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="apps-update-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pipeline-update-modal-head">
                <h2 id="apps-update-title" className="pipeline-update-modal-title">
                  Update {app.name || app.appType || app.instanceId}
                </h2>
                <button
                  type="button"
                  className="pipeline-update-modal-close"
                  aria-label="Close"
                  onClick={() => !updating && setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
              <div className="pipeline-update-modal-body">
                <p className="pipeline-update-version-line">
                  <span className="pipeline-update-version-from">
                    v{app.agentOpsVersion || '?'}
                  </span>
                  <span className="pipeline-update-version-arrow">→</span>
                  <span className="pipeline-update-version-to">
                    v{app.latestAgentOpsVersion || '?'}
                  </span>
                </p>
                {/* Apps don't currently surface per-version release
                    notes from the agent-ops repo. Once we wire a
                    GitHub Releases pull-through, drop notes in here. */}
                <p className="pipeline-update-no-notes">
                  Release notes are published in the agent-ops repo.
                </p>
                <p className="pipeline-update-preserved">
                  Your EFS volume, environment variables, and app data
                  are preserved. The container will restart while the
                  new image rolls out (~1 min).
                </p>
              </div>
              <div className="pipeline-update-modal-actions">
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--ghost"
                  onClick={() => setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--primary"
                  onClick={handleUpdate}
                  disabled={updating}
                >
                  <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
                  {updating ? 'Updating…' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab strip — reuse the same classes PipelineDetailPage uses
            so the visual treatment is identical. */}
        <div className="pipeline-detail-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pipeline-detail-tab ${activeTab === tab.id ? 'pipeline-detail-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="pipeline-detail-body">
          {activeTab === 'overview' && (
            <OverviewTab
              app={app}
              instanceId={instanceId}
              publicUrl={publicUrl}
              onCopy={copyToClipboard}
            />
          )}
          {activeTab === 'logs' && (
            <LogsTab app={app} instanceId={instanceId} />
          )}
          {activeTab === 'env' && (
            <EnvTab instanceId={instanceId} />
          )}
          {activeTab === 'conversations' && (
            <ConversationsTab instanceId={instanceId} appStatus={app.status} />
          )}
          {activeTab === 'settings' && (
            <div className="pipeline-tab">
              <div className="page-card app-detail-card">
                <DangerZone
                  separated={false}
                  label={app.displayName || app.name || app.appType || instanceId}
                  hint="This stops the running task and removes the app. This cannot be undone."
                  buttonLabel="Destroy app"
                  confirmLabel="Destroy"
                  busyLabel="Destroying…"
                  onConfirm={destroyApp}
                />
              </div>
            </div>
          )}
        </div>

      </div>
    </>
  );
};

// ─── Solo (dedicated-EC2) detail page ───────────────────────────────────
//
// Solo apps live on a dedicated EC2, are keyed by `appSlug` (not the cloud
// instanceId), and load from GET /apps/solo/{slug}/status. They don't share
// the fleet ALB/EFS, so the cloud tabs (logs / env / runs — all instanceId-
// keyed endpoints) don't apply. This is a deliberately minimal surface:
// header (title + status) + an Overview meta row (URL / region / framework /
// instance) + the Dedicated Static IP addon control. Reuses the same
// pipeline-detail / pipeline-stat chrome so it reads as a sibling of the
// cloud detail page.
const SoloAppDetailPage = ({ slug, projectId }) => {
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchApp = useCallback(async () => {
    setLoading(true);
    try {
      const data = await Apps.getSoloStatus(slug);
      // Shape into the object the header + StaticIpSection expect. A missing
      // row comes back as { appSlug, phase:'unknown' } with no status — treat
      // that as not-found so we render the 404 rather than an empty shell.
      if (!data || (!data.status && data.phase === 'unknown' && !data.instanceId)) {
        setError(new Error('not found'));
        setApp(null);
      } else {
        setApp({
          ...data,
          deployMode: 'solo',
          appSlug: data.appSlug || slug,
          // Solo status surfaces the finer-grained provisioning `phase`
          // while active; fall back to the lifecycle `status` otherwise —
          // same precedence AppsPage's pill uses.
          status: data.status === 'active' ? (data.phase || data.status) : (data.status || data.phase),
        });
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch solo app:', err);
      setError(err);
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchApp(); }, [fetchApp]);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setToast?.({ isOpen: true, message: `Copied ${label}`, color: 'success' });
    });
  };

  // Destroy lives in the Danger zone section at the bottom of the page
  // (parity with the pipeline + cloud-app Settings danger zones). The
  // DangerZone owns the type-to-confirm modal + busy state; this is its
  // onConfirm. Re-throws on failure so the modal stays open for a retry.
  const destroyApp = async () => {
    if (!app) return;
    try {
      await Apps.destroySolo(app.appSlug);
      setToast?.({ isOpen: true, message: 'App destroyed — VM, EBS volume + S3 backup deleted', color: 'success' });
      navigate(`/projects/${projectId}/apps`);
    } catch (err) {
      setToast?.({ isOpen: true, message: `Destroy failed: ${err.message}`, color: 'danger' });
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="pipeline-detail-page" aria-busy="true" aria-label="Loading app">
        <div className="pipeline-detail-skel-header">
          <div className="pipeline-detail-skel pipeline-detail-skel--title" />
          <div className="pipeline-detail-skel pipeline-detail-skel--meta" />
        </div>
        <div className="pipeline-detail-skel-body">
          <div className="pipeline-detail-skel pipeline-detail-skel--row" />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '85%' }} />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <NotFoundPage
        code="404"
        title="App not found"
        subtitle="This dedicated app may have been destroyed, or you don't have access to it."
        primaryAction={{
          label: 'Back to apps',
          to: `/projects/${projectId}/apps`,
          icon: arrowBackOutline,
        }}
      />
    );
  }

  // Solo status carries only the canonical `domain` (<slug>.apps.zibby.dev),
  // not the live publicIp — derive the URL from domain (https; TLS is
  // terminated on the VM by Caddy).
  const publicUrl = app.domain ? `https://${app.domain}` : null;
  const publicUrlNoScheme = publicUrl ? publicUrl.replace(/^https?:\/\//, '') : null;

  return (
    <>
      <Helmet>
        <title>{`${app.appSlug} · Apps · Zibby`}</title>
      </Helmet>

      <div className="pipeline-detail-page">
        <div className="pipeline-detail-header">
          <div className="pipeline-detail-titlerow">
            <h1 className="pipeline-detail-title">{app.appSlug}</h1>
            <span className="pipeline-detail-meta-sep">·</span>
            <AppStatus status={app.status} variant="header" />
            <span className="pipeline-detail-meta-sep">·</span>
            <span className="apps-deploy-badge" data-mode="solo">Dedicated</span>
            {app.tier && (
              <>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-version">{app.tier}</span>
              </>
            )}
            {app.updatedAt && (
              <>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-time">Updated {timeAgo(app.updatedAt)}</span>
              </>
            )}
          </div>
          <div className="pipeline-detail-actions">
            {publicUrl && (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="header-docs-link"
              >
                <IonIcon icon={openOutline} />
                <span>Open</span>
              </a>
            )}
            {/* Destroy moved into the Danger zone section below (parity
                with pipelines + cloud apps). */}
          </div>
        </div>

        <div className="pipeline-detail-body">
          <div className="pipeline-tab">
            <div className="pipeline-stat-grid">
              {publicUrl && (
                <div className="pipeline-stat">
                  <div className="pipeline-stat-label">Public URL</div>
                  <div className="pipeline-stat-value">
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="pipeline-stat-value-text"
                    >
                      {publicUrlNoScheme}
                    </a>
                    <button
                      type="button"
                      className="pipeline-stat-copy"
                      onClick={() => copyToClipboard(publicUrl, 'public URL')}
                      title="Copy URL"
                    >
                      <IonIcon icon={copyOutline} />
                    </button>
                  </div>
                </div>
              )}

              <div className="pipeline-stat">
                <div className="pipeline-stat-label">Status</div>
                <div className="pipeline-stat-value">
                  <span className="pipeline-stat-value-text">{app.status || '—'}</span>
                </div>
                {app.detail && <div className="app-overview-substat">{app.detail}</div>}
              </div>

              <div className="pipeline-stat">
                <div className="pipeline-stat-label">Region</div>
                <div className="pipeline-stat-value">
                  <span className="pipeline-stat-value-text">{app.region || '—'}</span>
                </div>
                {app.framework && (
                  <div className="app-overview-substat">{app.framework}</div>
                )}
              </div>

              <div className="pipeline-stat">
                <div className="pipeline-stat-label">Instance</div>
                <div className="pipeline-stat-value">
                  <span className="pipeline-stat-value-text">{app.instanceId || '—'}</span>
                  {app.instanceId && (
                    <button
                      type="button"
                      className="pipeline-stat-copy"
                      onClick={() => copyToClipboard(app.instanceId, 'instance id')}
                      title="Copy ID"
                    >
                      <IonIcon icon={copyOutline} />
                    </button>
                  )}
                </div>
                {app.instanceType && (
                  <div className="app-overview-substat">{app.instanceType}</div>
                )}
              </div>

              {typeof app.monthlyUSD === 'number' && (
                <div className="pipeline-stat">
                  <div className="pipeline-stat-label">Monthly cost</div>
                  <div className="pipeline-stat-value">
                    <span className="pipeline-stat-value-text">${app.monthlyUSD}/mo</span>
                  </div>
                </div>
              )}
            </div>

            {/* Dedicated Static IP addon — solo apps only. Same control the
                cloud Overview tab gates on deployMode==='solo' && appSlug. */}
            {app.appSlug && <StaticIpSection app={app} />}

            {/* Danger zone — type-to-confirm delete (data loss: VM + EBS +
                S3). Same shared component the pipeline + cloud-app settings
                use. */}
            <DangerZone
              label={app.appSlug}
              requireType
              heading="Danger zone"
              hint="Deleting terminates the VM and deletes ALL data — the EBS volume and the S3 (Litestream) backup. This cannot be undone."
              buttonLabel="Delete app"
              confirmLabel="Permanently delete"
              busyLabel="Deleting…"
              onConfirm={destroyApp}
            />
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Overview tab ──────────────────────────────────────────────────────
// Horizontal meta row (URL / instance / running / resources / storage /
// hourly cost) + the merged-in Config sections below it. Reuses the
// `pipeline-stat-grid` + `pipeline-stat` classes from PipelineDetailPage
// so the two detail pages look like siblings. Per-section bodies below
// the meta row reuse `pipeline-section` / `pipeline-section-title`.

const OverviewTab = ({ app, instanceId, publicUrl, onCopy }) => {
  const [catalog, setCatalog] = useState(null);
  const [, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [storage, setStorage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Goal-based deploys (free-form natural-language install, not a catalog
    // entry) have no appType. Skip the catalog fetch — it would 4xx with
    // "appType is required" and surface a misleading warning to the user
    // who never asked for a catalog app.
    if (!app.appType) { setCatalogLoading(false); return undefined; }
    (async () => {
      try {
        const data = await Apps.getCatalogEntry(app.appType);
        if (!cancelled) setCatalog(data);
      } catch (err) {
        if (!cancelled) setCatalogError(err.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [app.appType]);

  useEffect(() => {
    let cancelled = false;
    const fetchStorage = async () => {
      try {
        const data = await Apps.getStorage(instanceId);
        if (!cancelled) setStorage(data);
      } catch (err) {
        // Storage is non-fatal — render `—` and move on.
        if (!cancelled) setStorage({ usedBytes: null });
      } finally {
        if (!cancelled) setStorageLoading(false);
      }
    };
    fetchStorage();
    // Poll at the same cadence agent-ops emits (60s) so the displayed
    // number stays approximately fresh while the user has the tab open.
    const id = setInterval(fetchStorage, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [instanceId]);

  // Resources come from the catalog (the immutable shape the app was
  // deployed against); fall back to whatever the row carries so we
  // still render something while the catalog is loading or if it's
  // gone missing.
  const cpu = catalog?.resources?.cpu ?? app.resources?.cpu;
  const memory = catalog?.resources?.memory ?? app.resources?.memory;
  const cpuVcpu = cpu ? (cpu / 1024).toFixed(cpu % 1024 === 0 ? 0 : 2) : null;
  const memGb = memory ? (memory / 1024).toFixed(memory % 1024 === 0 ? 0 : 2) : null;
  const resourcesText = (cpuVcpu || memGb)
    ? `${cpuVcpu ? `${cpuVcpu} vCPU` : ''}${cpuVcpu && memGb ? ' · ' : ''}${memGb ? `${memGb} GB` : ''}`
    : '—';

  const tier = catalog?.pricing?.tier || app.pricing?.tier || 'standard';
  const tierInfo = resolveTier(tier);

  // agent-ops hard-codes two scheduler entries on first-run bootstrap
  // (visible in agent-ops logs as `hourly_health_check` and
  // `weekly_upgrade`). They don't come from the catalog — they're
  // baked into agent-ops itself — so the strings are hardcoded here.
  // If/when agent-ops exposes a list-tasks MCP tool the right thing
  // to do is query it; for now this is correct because agent-ops
  // always wires these two.
  const schedulerCrons = [
    { id: 'hourly_health_check', cron: '0 * * * *',  description: 'Verify the app is responding; restart on failure.' },
    { id: 'weekly_upgrade',      cron: '0 9 * * 1',  description: 'Pull the latest published catalog version and redeploy.' },
  ];

  const publicUrlNoScheme = publicUrl ? publicUrl.replace(/^https?:\/\//, '') : null;

  return (
    <div className="pipeline-tab">
      {/* Meta row — same flex-wrap stat grid PipelineDetailPage uses.
          Each `.pipeline-stat` is a column (label on top in small caps
          grey, value below). The grid wraps on narrow viewports via
          `flex-wrap: wrap` on `.pipeline-stat-grid`. */}
      <div className="pipeline-stat-grid">
        {publicUrl && (
          <div className="pipeline-stat">
            <div className="pipeline-stat-label">Public URL</div>
            <div className="pipeline-stat-value">
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="pipeline-stat-value-text"
              >
                {publicUrlNoScheme}
              </a>
              <button
                type="button"
                className="pipeline-stat-copy"
                onClick={() => onCopy(publicUrl, 'public URL')}
                title="Copy URL"
              >
                <IonIcon icon={copyOutline} />
              </button>
            </div>
          </div>
        )}

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Instance ID</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{app.instanceId}</span>
            <button
              type="button"
              className="pipeline-stat-copy"
              onClick={() => onCopy(app.instanceId, 'instance id')}
              title="Copy ID"
            >
              <IonIcon icon={copyOutline} />
            </button>
          </div>
          {/* CPU architecture under the instance id — small ALL-CAPS
              substat so it reads as a metadata tag, not a stat value.
              Hidden when the field is missing (older rows pre-rollout). */}
          {app.architecture && (
            <div className="app-overview-substat">
              {String(app.architecture).toUpperCase().replace('_', '-')}
            </div>
          )}
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Running</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">
              {(app.runningCount ?? 1)} / {(app.desiredCount ?? 1)}
            </span>
          </div>
          <AppStatus
            status={app.status}
            runningCount={app.runningCount}
            desiredCount={app.desiredCount}
          />
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Resources</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{resourcesText}</span>
          </div>
        </div>

        {/* Storage — actual EFS usage sourced from agent-ops's
            `efs_usage` slog lines. AWS doesn't publish per-access-point
            usage as a metric, so the daemon `du`-walks $StateDir every
            60s and emits a JSON log row; backend filters CloudWatch for
            the newest line. Null while we wait for the first sample
            (fresh deploy / agent-ops <0.1.18). */}
        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Storage</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">
              {storageLoading
                ? '…'
                : (storage && typeof storage.usedBytes === 'number')
                  ? formatBytes(storage.usedBytes)
                  : '—'}
            </span>
          </div>
          <div className="app-overview-substat">
            {storage && storage.sampleAt
              ? `EFS · ${timeAgo(storage.sampleAt)}`
              : 'EFS · auto-scaling'}
          </div>
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Hourly cost</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{tierInfo.hourly}</span>
          </div>
          <div className="app-overview-substat">{tierInfo.label}</div>
        </div>
      </div>

      {/* Catalog error banner — non-fatal; the meta row above still
          renders from `app.*`, but the sections below depend on the
          catalog. Keep the user informed instead of silently empty. */}
      {catalogError && (
        <p className="apps-modal-helper app-logs-error">⚠ {catalogError}</p>
      )}

      {/* Bootstrap script + Env vars + Setup hint removed from end-user
          view — these are catalog internals (install commands, default
          host/port env, prose explaining n8n's onboarding). Users can't
          change them and don't need to see them. Available via CLI:
          `zbx app templates` shows the catalog entry verbatim. */}

      {/* agent-ops scheduler */}
      <div className="page-card app-detail-card">
        <div className="card-header"><h2>agent-ops scheduler</h2></div>
        <p className="apps-modal-helper">
          Every Zibby-managed app runs an in-container scheduler. These two
          jobs are wired automatically; they cannot be edited from the
          dashboard yet.
        </p>
        <table className="app-config-envs">
          <thead>
            <tr><th>Job</th><th>Cron</th><th>Translated</th></tr>
          </thead>
          <tbody>
            {schedulerCrons.map((c) => (
              <tr key={c.id}>
                <td><code className="app-detail-mono">{c.id}</code></td>
                <td><code className="app-detail-mono">{c.cron}</code></td>
                <td>
                  {safeCronstrue(c.cron)}
                  <div className="app-config-cron-hint">{c.description}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dedicated Static IP addon — SOLO apps only. Cloud apps share the
          fleet ALB and have no per-app EIP, so the control is meaningless
          there; gate strictly on deployMode==='solo'. */}
      {app.deployMode === 'solo' && app.appSlug && (
        <StaticIpSection app={app} />
      )}
    </div>
  );
};

// ─── Dedicated Static IP addon section (solo apps) ───────────────────────
//
// Per-app addon control, modeled on PipelineDetailPage's WarmPoolSection:
// loading → spinner; inactive → "Shared IP" + an enable button (gated by a
// confirm modal + a 402 InsufficientCreditsModal); active → "Dedicated
// static IP" + the current IP + a disable button. Reads the rate from the
// status response (pricing.perMinuteMilliCents) so the price isn't hard-
// coded here — the backend's plans.js / pricing-rates.js are the SSOT.
function StaticIpSection({ app }) {
  const slug = app.appSlug;
  const [status, setStatus] = useState('loading'); // 'loading' | 'inactive' | 'active' | 'error'
  const [staticIp, setStaticIp] = useState(null);
  // Per-minute milli-cent rate — fetched from status so a per-account VIP
  // override stays in sync. Falls back to 12 (the published $5/mo default)
  // only for the moment between mount and first response.
  const [perMinuteRate, setPerMinuteRate] = useState(12);
  const [priceUSD, setPriceUSD] = useState(5);
  const [busy, setBusy] = useState(false);
  const [credsModal, setCredsModal] = useState({ open: false, required: null, balance: null });
  const [confirmEnable, setConfirmEnable] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await Apps.getStaticIpStatus(slug);
      const rate = Number(data?.pricing?.perMinuteMilliCents);
      if (Number.isFinite(rate) && rate > 0) setPerMinuteRate(rate);
      const usd = Number(data?.pricing?.priceUSD);
      if (Number.isFinite(usd) && usd > 0) setPriceUSD(usd);
      if (data?.enabled) {
        setStatus('active');
        setStaticIp(data.staticIp || null);
      } else {
        setStatus('inactive');
        setStaticIp(null);
      }
    } catch (err) {
      console.error('[static-ip] status fetch failed:', err);
      setStatus('error');
    }
  }, [slug]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await Apps.enableStaticIp(slug);
      Credits.fireChangedEvent();
      setStatus('active');
      setStaticIp(res?.staticIp || null);
    } catch (err) {
      if (err?.status === 402) {
        const body = err.body || {};
        setCredsModal({
          open: true,
          required: Number.isFinite(Number(body.requiredMilliCents)) ? Number(body.requiredMilliCents) : null,
          balance: Number.isFinite(Number(body.balanceMilliCents)) ? Number(body.balanceMilliCents) : null,
        });
      } else {
        console.error('[static-ip] enable failed:', err);
        await fetchStatus();
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (busy) return;
    if (typeof window !== 'undefined'
        && !window.confirm('Disable the dedicated static IP? The IP will be released on the next pause and may change on resume. Per-minute charges stop now.')) {
      return;
    }
    setBusy(true);
    try {
      await Apps.disableStaticIp(slug);
      Credits.fireChangedEvent();
      setStatus('inactive');
      setStaticIp(null);
    } catch (err) {
      console.error('[static-ip] disable failed:', err);
      await fetchStatus();
    } finally {
      setBusy(false);
    }
  };

  const perDay = perMinuteRate * 60 * 24;

  return (
    <div className="page-card app-detail-card" data-testid="static-ip-section">
      <div className="card-header"><h2>Dedicated static IP</h2></div>
      <p className="apps-modal-helper">
        By default this app’s public IP is released when it’s paused and a new
        one is assigned on resume. Enable a dedicated static IP to keep the
        same address across pause/resume — so you can safely point an external
        DNS A record at it. Paid add-on: ${priceUSD}/mo ({formatRate(perMinuteRate)}),
        billed per minute while enabled.
      </p>

      {status === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <IonSpinner name="dots" />
          <span className="apps-modal-helper" style={{ margin: 0 }}>Loading…</span>
        </div>
      )}

      {status === 'error' && (
        <p className="apps-modal-helper" style={{ color: 'rgba(248, 113, 113, 0.85)' }}>
          Could not load static-IP status.{' '}
          <button type="button" className="warm-pool-link-btn" onClick={fetchStatus}>Retry</button>
        </p>
      )}

      {status === 'inactive' && (
        <>
          <p className="apps-modal-helper" data-testid="static-ip-state">
            Current mode: <strong>Shared IP</strong> (released on pause)
          </p>
          <IonButton
            fill="solid"
            size="default"
            disabled={busy}
            onClick={() => setConfirmEnable(true)}
            data-testid="static-ip-enable"
          >
            <IonIcon slot="start" icon={globeOutline} />
            Enable dedicated static IP ({formatRate(perMinuteRate)})
          </IonButton>
        </>
      )}

      {status === 'active' && (
        <>
          <p className="apps-modal-helper" style={{ margin: '0 0 4px' }} data-testid="static-ip-state">
            Current mode: <strong>Dedicated static IP</strong>
            {staticIp ? <> · <code className="app-detail-mono">{staticIp}</code></> : ''}
          </p>
          <p className="apps-modal-helper" style={{ margin: '0 0 12px' }}>
            Rate: <strong>{formatRate(perMinuteRate)}</strong>
            {' '}· Per-day estimate: <strong>{formatBalance(perDay)}</strong>
          </p>
          <DangerButton icon={trashOutline} onClick={disable} disabled={busy}>
            Disable dedicated static IP
          </DangerButton>
        </>
      )}

      <InsufficientCreditsModal
        open={credsModal.open}
        onClose={() => setCredsModal((s) => ({ ...s, open: false }))}
        title="Add credits to enable a dedicated static IP"
        ariaLabel="Insufficient credits to enable dedicated static IP"
        actionLabel="enable a dedicated static IP"
        rateLabel={formatRate(perMinuteRate)}
        minRuntimeLabel="1 day"
        minRequiredMilliCents={credsModal.required}
        balanceMilliCents={credsModal.balance}
        primaryLabel="Go to Billing"
        primaryTo="/billing"
        secondaryLabel="Cancel"
      />

      <MktAlertModal
        open={confirmEnable}
        onClose={() => setConfirmEnable(false)}
        title="Enable dedicated static IP?"
        ariaLabel="Confirm dedicated static IP enable"
        primaryLabel="Yes, enable"
        onPrimary={() => { setConfirmEnable(false); enable(); }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>
          This app’s public IP will be <strong style={{ color: '#fff' }}>retained across pause/resume</strong>,
          so an external DNS A record pointed at it stays valid.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Per-minute charges begin immediately at{' '}
          <strong style={{ color: '#fff' }}>{formatRate(perMinuteRate)}</strong>
          {' '}(~{formatBalance(perDay)} per day). You can disable any time to stop charges.
        </p>
      </MktAlertModal>
    </div>
  );
}

// ─── Logs tab ──────────────────────────────────────────────────────────
// Polling-based tail of the agent-ops + app container logs via
// GET /apps/:id/logs. Unchanged from v1 — only moved into a tab body.
// Hidden entirely for instances that can't have logs (stub-mode
// services, stopped/destroyed rows).

const LogsTab = ({ app, instanceId }) => {
  // Logs are paginated by CloudWatch `nextToken` (here called `cursor`).
  // First fetch returns the newest page; subsequent "load older" fetches
  // pass the prior cursor and PREPEND older lines onto the existing
  // buffer. Live-tail polling only runs on the newest page (cursor=null
  // AND filter cleared) — once the user starts browsing history we
  // freeze the polling so their scroll position doesn't get stomped.
  const [logs, setLogs] = useState([]);                 // flat array of "<iso>  <message>"
  const [cursor, setCursor] = useState(null);           // next page token (for older lines)
  const [hasMore, setHasMore] = useState(false);        // matches the last response's hasMore
  const [, setLogsLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [logsPaused, setLogsPaused] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logsFilter, setLogsFilter] = useState('');       // raw input value
  const [appliedFilter, setAppliedFilter] = useState(''); // debounced, server-side
  // Skeleton gate — true once the first fetch resolves (success or
  // error). Subsequent 3s polls / filter refetches don't flip it back,
  // so the skeleton only flashes on initial mount, not on every
  // refresh or every keystroke in the filter box.
  const [hasLoaded, setHasLoaded] = useState(false);
  const logsBodyRef = useRef(null);
  const stickyBottomRef = useRef(true);
  // Refs mirror the cursor/loading state for callbacks that close over
  // the initial render's values (scroll handler, polling loop).
  const cursorRef = useRef(cursor);
  const hasMoreRef = useRef(hasMore);
  const loadingOlderRef = useRef(loadingOlder);
  useEffect(() => { cursorRef.current = cursor; }, [cursor]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingOlderRef.current = loadingOlder; }, [loadingOlder]);

  // Debounce the user's keystrokes 250ms before kicking off a fresh
  // server-side filter query.
  useEffect(() => {
    const id = setTimeout(() => setAppliedFilter(logsFilter.trim()), 250);
    return () => clearTimeout(id);
  }, [logsFilter]);

  // Live-tail polling — only active when we're on the newest page
  // (cursor=null) so scrolling into history doesn't get clobbered every
  // 3s. Pause/Resume toggle still wins.
  useEffect(() => {
    if (!instanceId || !app) return undefined;
    const serviceArn = app.serviceArn || '';
    const isStub = serviceArn.includes(':stub:');
    if (isStub) return undefined;
    if (['stopped', 'destroyed', 'failed'].includes(app.status)) return undefined;
    if (logsPaused) return undefined;

    let cancelled = false;
    // Track whether the first fetch has landed — we always run the
    // initial fetchLatest unconditionally so the panel populates, but
    // subsequent interval ticks honour the stickyBottom gate.
    let firstFetchDone = false;
    const fetchLatest = async () => {
      const node = logsBodyRef.current;
      if (node) {
        const slack = 24;
        stickyBottomRef.current = (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      }
      // If the user has scrolled away from the bottom (browsing
      // history, reading mid-buffer, scrolled up to trigger
      // load-older), DON'T replace the buffer — fetchLatest fetches
      // just the latest 200 lines, which would wipe any older pages
      // the user loaded via the top sentinel and cause the viewport
      // to jump as content rewrites underneath them.
      if (firstFetchDone && !stickyBottomRef.current) return;
      setLogsLoading(true);
      try {
        const data = await Apps.getLogs(instanceId, {
          lines: 200,
          filter: appliedFilter || undefined,
        });
        if (cancelled) return;
        const lines = Array.isArray(data?.lines) ? data.lines : [];
        setLogs(lines);
        setCursor(data?.nextToken || null);
        setHasMore(!!data?.hasMore);
        setLogsError(null);
      } catch (err) {
        if (cancelled) return;
        setLogsError(err.message || 'Failed to fetch logs');
      } finally {
        if (!cancelled) {
          setLogsLoading(false);
          setHasLoaded(true);
          firstFetchDone = true;
        }
      }
    };

    fetchLatest();
    const id = setInterval(fetchLatest, 3000);
    return () => { cancelled = true; clearInterval(id); };
    // appliedFilter triggers a re-fetch from scratch when the user
    // changes the search box (debounced upstream).
  }, [instanceId, app, logsPaused, appliedFilter]);

  // Load-older handler — kicks off when the scroll position is near the
  // top of the buffer (or via the IntersectionObserver sentinel below).
  // Guarded by hasMore + loadingOlder to avoid duplicate fan-out.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current) return;
    if (!hasMoreRef.current) return;
    if (!cursorRef.current) return;
    setLoadingOlder(true);
    // Remember the current scroll offset so we can restore it after
    // prepending older lines (otherwise the viewport jumps up).
    const node = logsBodyRef.current;
    const prevScrollHeight = node ? node.scrollHeight : 0;
    const prevScrollTop = node ? node.scrollTop : 0;
    try {
      const data = await Apps.getLogs(instanceId, {
        lines: 200,
        cursor: cursorRef.current,
        filter: appliedFilter || undefined,
      });
      const olderLines = Array.isArray(data?.lines) ? data.lines : [];
      setLogs((prev) => [...olderLines, ...prev]);
      setCursor(data?.nextToken || null);
      setHasMore(!!data?.hasMore);
      // After React paints the prepended rows, restore scroll position
      // so the user's viewport stays anchored on the line they were
      // looking at — they only see new content sliding in from the top.
      requestAnimationFrame(() => {
        const n2 = logsBodyRef.current;
        if (!n2) return;
        const delta = n2.scrollHeight - prevScrollHeight;
        n2.scrollTop = prevScrollTop + delta;
      });
    } catch (err) {
      setLogsError(err.message || 'Failed to fetch older logs');
    } finally {
      setLoadingOlder(false);
    }
  }, [instanceId, appliedFilter]);

  // Scroll-position detector: when the user scrolls within 50px of the
  // top of the buffer, request the next older page. Cheap onScroll +
  // ref check (no IntersectionObserver needed for one trigger zone).
  useEffect(() => {
    const node = logsBodyRef.current;
    if (!node) return undefined;
    const onScroll = () => {
      if (node.scrollTop < 50) loadOlder();
    };
    node.addEventListener('scroll', onScroll);
    return () => node.removeEventListener('scroll', onScroll);
  }, [loadOlder]);

  // Auto-scroll to bottom on live-tail updates only — and only when the
  // user is already near the bottom (sticky-tail). If they've scrolled
  // up to browse history, leave them where they are.
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    const node = logsBodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [logs]);

  const isStub = String(app.serviceArn || '').includes(':stub:');
  const isFinal = ['stopped', 'destroyed', 'failed'].includes(app.status);
  if (isStub || isFinal) {
    return (
      <div className="pipeline-tab">
        <div className="page-card app-detail-card">
          <div className="card-header"><h2>Instance log</h2></div>
          <p className="apps-modal-helper">
            {isFinal
              ? `This instance is ${app.status} — no live logs to tail.`
              : 'Logs are not available for stub instances.'}
          </p>
        </div>
      </div>
    );
  }

  // Pre-compute the widest line-number column so all numbers align.
  const gutterWidth = Math.max(3, String(logs.length).length);
  // Live-polling indicator: mirror the EXACT conditions the polling
  // useEffect uses to decide whether to keep ticking. `cursor` is set on
  // every successful fetch (CloudWatch's nextToken) so it's almost never
  // null — that's not the right gate. The real gates are: app loaded,
  // not a stub service, status is alive, and user hasn't paused.
  const isPollingLive = !logsPaused
    && !!app
    && !(app.serviceArn || '').includes(':stub:')
    && !['stopped', 'destroyed', 'failed'].includes(app.status);
  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        {/* No <h2> title — the tab strip above already reads
            'INSTANCE LOG', a card-level title repeats it. Filter input
            takes the title slot on the left; status + control buttons
            sit on the right. */}
        <div className="card-header app-logs-header">
          <input
            type="text"
            className="app-logs-ctl app-logs-ctl--input"
            placeholder="Filter…"
            value={logsFilter}
            onChange={(e) => setLogsFilter(e.target.value)}
            aria-label="Filter logs"
          />
          <div className="app-logs-controls">
            {isPollingLive && (
              <span className="app-logs-live" title="Polling every 3s">Live</span>
            )}
            <button
              type="button"
              className="app-logs-ctl app-logs-ctl--btn"
              onClick={() => setLogsPaused((p) => !p)}
              aria-pressed={logsPaused}
            >
              <IonIcon icon={logsPaused ? playOutline : pauseOutline} />
              <span>{logsPaused ? 'Start' : 'Stop'}</span>
            </button>
          </div>
        </div>

        <pre ref={logsBodyRef} className="app-logs-body">
          {/* Top sentinel — load-older indicator and end-of-history mark. */}
          {hasLoaded && logs.length > 0 && (
            <div className="app-logs-loader">
              {loadingOlder
                ? 'Loading older lines…'
                : (hasMore ? 'Scroll up to load older lines…' : '— End of history —')}
            </div>
          )}
          {!hasLoaded ? (
            // Initial-load skeleton: 8 monospace shimmer rows with a
            // matching gutter so when the real logs paint there's no
            // layout shift. Random-ish widths (66/82/74/…) so it reads
            // as "varied log lines", not three identical bars.
            <div className="app-logs-skeleton" aria-hidden>
              {[66, 82, 74, 90, 58, 78, 86, 64].map((w, i) => (
                <div key={i} className="app-logs-line app-logs-skel-line">
                  <span
                    className="app-logs-lineno app-logs-skel-lineno"
                    style={{ width: '3ch' }}
                  />
                  <span
                    className="app-logs-linetext app-logs-skel-bar"
                    style={{ width: `${w}%` }}
                  />
                </div>
              ))}
            </div>
          ) : logsError ? (
            <span className="app-logs-error">⚠ {logsError}</span>
          ) : logs.length === 0 ? (
            <span className="app-logs-empty">
              {appliedFilter
                ? `No lines match "${appliedFilter}".`
                : (app.status === 'provisioning'
                  ? 'Waiting for agent-ops to emit first log line…'
                  : 'No log events found.')}
            </span>
          ) : (
            logs.map((line, i) => {
              const p = parseLogLine(line);
              return (
                <div key={i} className={`app-logs-line app-logs-line--${p.severity}`}>
                  <span className="app-logs-lineno" style={{ width: `${gutterWidth}ch` }}>
                    {i + 1}
                  </span>
                  <span className="app-logs-linetext">
                    {p.ts && <span className="app-logs-ts">{p.ts}</span>}
                    {p.level && (
                      <span className={`app-logs-level app-logs-level--${p.level.toLowerCase()}`}>
                        {p.level}
                      </span>
                    )}
                    {p.msg && <span className="app-logs-msg">{p.msg}</span>}
                    {p.body && <span className="app-logs-body-txt">{p.body}</span>}
                  </span>
                </div>
              );
            })
          )}
        </pre>
      </div>
    </div>
  );
};

// ─── ENV tab ───────────────────────────────────────────────────────────
//
// Read-only, suffix-only view of the per-instance env block. Backed by
// GET /apps/:instanceId/env (backend handler `getAppEnv` in apps.js):
//
//   - identity[]    — allow-listed plaintext breadcrumbs (INSTANCE_ID,
//                     ZIBBY_PROJECT_ID, AGENT_OPS_PROVIDER, …).
//   - credentials[] — allow-listed sensitive keys; each row is
//                     { key, present, suffix } with suffix = "…abc123".
//                     Plaintext NEVER crosses the wire.
//
// No edit UI in v1 — to rotate Claude, the user updates their
// workspace-credentials (Settings paste-token card) then runs
// `zibby app update-credential <id>` from the CLI.
const EnvTab = ({ instanceId }) => {
  const [env, setEnv] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Apps.getEnv(instanceId)
      .then((data) => { if (!cancelled) setEnv(data || null); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load env'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [instanceId]);

  // Mirror the workflow ENV tab atom-by-atom (see PipelineDetailPage.js
  // around line 1592–1624): `.pipeline-tab` shell + `.pipeline-env-header`
  // hint row + `<DataTable>` for the rows. No inline styles — the table
  // chrome and the env-cell typography (`.pipeline-env-key`,
  // `.pipeline-env-mask*`) all come from the shared pipelines stylesheet
  // we already import at the top of this file.

  const credentials = env?.credentials || [];
  const identity = env?.identity || [];

  const credentialColumns = [
    {
      header: 'KEY',
      key: 'key',
      render: (r) => <code className="pipeline-env-key">{r.key}</code>,
    },
    {
      header: 'SUFFIX',
      key: 'suffix',
      render: (r) => (
        r.present
          ? (
            <span className="pipeline-env-mask">
              <span className="pipeline-env-mask-dots" aria-hidden="true">{'•'.repeat(24)}</span>
              <span className="pipeline-env-mask-suffix">{r.suffix}</span>
            </span>
          )
          : <span className="pipeline-env-mask"><em style={{ opacity: 0.5 }}>not set</em></span>
      ),
    },
  ];

  const identityColumns = [
    {
      header: 'KEY',
      key: 'key',
      render: (r) => <code className="pipeline-env-key">{r.key}</code>,
    },
    {
      header: 'VALUE',
      key: 'value',
      render: (r) => <span className="pipeline-env-mask">{r.value || '—'}</span>,
    },
  ];

  return (
    <div className="pipeline-tab">
      <div className="pipeline-env-header">
        <p className="pipeline-section-hint">
          Sensitive tokens — values are masked to the last 6 characters so you
          can confirm the right credential is wired up without exposing the
          plaintext. To rotate: paste a new token in{' '}
          <a href="/integrations">Settings → Integrations</a>, then run{' '}
          <code>zibby app update-credential {instanceId}</code> from the CLI.
        </p>
      </div>
      <DataTable
        data={credentials}
        columns={credentialColumns}
        loading={loading}
        rowKey="key"
        emptyState={{
          title: error || 'No credentials',
          description: error
            ? 'Could not load the instance env. Try refreshing.'
            : 'No credential env vars present on this instance yet — likely still provisioning.',
        }}
      />

      <div className="pipeline-env-header" style={{ marginTop: 24 }}>
        <p className="pipeline-section-hint">
          Non-sensitive env vars the agent uses to identify itself + route
          outbound calls. Plaintext is safe to display.
        </p>
      </div>
      <DataTable
        data={identity}
        columns={identityColumns}
        loading={loading}
        rowKey="key"
        emptyState={{
          title: 'No identity vars',
          description: 'Identity env is populated once the task is running.',
        }}
      />
    </div>
  );
};

// ─── Agent activity tab ────────────────────────────────────────────────
// Reads structured run records from the control plane
// (GET /apps/:id/runs → the `zibby-{stage}-app-runs` DynamoDB table the
// agent-ops daemon pushes to after every finished run). This replaced
// the old approach of grepping CloudWatch for `claudecli: conversation
// complete` lines, which silently dropped failed runs (they emit no
// such line) and only ever surfaced the earliest few records before the
// log budget ran out. See AGENT_RUNS_PLAN.md for the full contract.

/**
 * ConversationsTab — agent run history viewer.
 *
 * List (lightweight): GET /apps/:id/runs?limit=50, newest first. Polled
 * every 10s (runs are ~hourly, so no need for a 3s tail cadence). Each
 * row shows local time · headline (taskName / summary) · a status badge
 * · turns · cost — including FAILED/TIMEOUT runs, which the log-grep
 * approach couldn't see.
 *
 * Detail (heavy): expanding a row lazy-fetches GET /apps/:id/runs/:runId
 * (the list omits systemPrompt / userPrompt / result to stay cheap) and
 * caches it per runId so re-expanding doesn't refetch.
 */

// status → badge class + label. completed=green, failed=red,
// timeout/canceled=amber, running=muted/pulse. Reuses the existing log
// colour tokens via the `.app-convo-badge--*` classes in the stylesheet
// (no new palette).
const RUN_STATUS_BADGE = {
  completed: { cls: 'app-convo-badge--ok',     label: 'completed' },
  failed:    { cls: 'app-convo-badge--err',    label: 'failed' },
  timeout:   { cls: 'app-convo-badge--warn',   label: 'timeout' },
  canceled:  { cls: 'app-convo-badge--warn',   label: 'canceled' },
  running:   { cls: 'app-convo-badge--running', label: 'running' },
};

const ConversationsTab = ({ instanceId, appStatus }) => {
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  // Per-runId detail cache: { [runId]: { loading, error, data } }. Keeps
  // an expanded row from refetching on re-expand and survives list polls.
  const [details, setDetails] = useState({});
  // hasLoaded gates the skeleton — true once the first fetch resolves
  // (success OR error). Subsequent 10s polls don't flip it, so the
  // skeleton only flashes on the initial mount, not on every refresh.
  const [hasLoaded, setHasLoaded] = useState(false);
  // Infinite scroll over older runs. Runs are newest-FIRST: the live poll
  // refreshes the top (newest) page; scrolling to the bottom appends OLDER
  // pages via the DDB cursor (`nextToken`). `cursor`/`hasMore` track the
  // OLDEST boundary loaded so far and are advanced only by loadOlder + the
  // first poll — never overwritten by later polls (which fetch the newest
  // page and would otherwise reset pagination).
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const PAGE = 50;
  const sentinelRef = useRef(null);
  // Refs mirror state for the IntersectionObserver / poll callbacks that
  // close over a stale render.
  const cursorRef = useRef(cursor);
  const hasMoreRef = useRef(hasMore);
  const loadingOlderRef = useRef(loadingOlder);
  const initializedRef = useRef(false);
  useEffect(() => { cursorRef.current = cursor; }, [cursor]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingOlderRef.current = loadingOlder; }, [loadingOlder]);

  // Reset pagination when the instance changes (switching apps).
  useEffect(() => {
    initializedRef.current = false;
    setRuns([]); setCursor(null); setHasMore(false);
    setHasLoaded(false); setError(null);
  }, [instanceId]);

  // Merge a freshly-polled newest page into the accumulated list,
  // newest-first, de-duped by runId. The newest page wins for any run it
  // also contains (fresher status/cost), and already-loaded OLDER pages
  // are preserved below it. (Runs are ~hourly, so a new run shifting the
  // page boundary at most duplicates one row across a fetch — dedupe
  // handles it; a missed in-between run is acceptable at this cadence.)
  const mergeNewest = (newest, prev) => {
    const ids = new Set(newest.map((r) => r.runId));
    return [...newest, ...prev.filter((r) => !ids.has(r.runId))];
  };

  // 10s polling — no pause toggle (agent runs land at the top hourly,
  // never clobbering an expanded row below). Skip when the instance is
  // stopped/destroyed. Newest-first ordering comes from the backend.
  useEffect(() => {
    if (!instanceId) return undefined;
    if (['stopped', 'destroyed', 'failed'].includes(appStatus)) return undefined;

    let cancelled = false;
    const fetchRuns = async () => {
      try {
        const data = await Apps.listRuns(instanceId, { limit: PAGE });
        if (cancelled) return;
        const fresh = Array.isArray(data?.runs) ? data.runs : [];
        if (!initializedRef.current) {
          // First successful load — seed the list AND the older-boundary
          // cursor from page 1.
          setRuns(fresh);
          setCursor(data?.nextToken || null);
          setHasMore(!!data?.hasMore);
          initializedRef.current = true;
        } else {
          // Subsequent polls only refresh the newest page; leave the
          // older-boundary cursor/hasMore untouched so loaded history
          // stays put.
          setRuns((prev) => mergeNewest(fresh, prev));
        }
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch runs');
      } finally {
        if (!cancelled) setHasLoaded(true);
      }
    };

    fetchRuns();
    const id = setInterval(fetchRuns, 10_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [instanceId, appStatus]);

  // Load the next OLDER page, appended to the bottom. Guarded by
  // hasMore + in-flight so the observer can't double-fire.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || !hasMoreRef.current || !cursorRef.current) return;
    setLoadingOlder(true);
    try {
      const data = await Apps.listRuns(instanceId, { limit: PAGE, cursor: cursorRef.current });
      const older = Array.isArray(data?.runs) ? data.runs : [];
      setRuns((prev) => {
        const ids = new Set(prev.map((r) => r.runId));
        return [...prev, ...older.filter((r) => !ids.has(r.runId))];
      });
      setCursor(data?.nextToken || null);
      setHasMore(!!data?.hasMore);
    } catch (err) {
      setError(err.message || 'Failed to load older runs');
    } finally {
      setLoadingOlder(false);
    }
  }, [instanceId]);

  // Infinite-scroll trigger: fire loadOlder when the bottom sentinel
  // scrolls into view. Re-attaches when the list (and thus the sentinel)
  // first renders.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadOlder();
    }, { rootMargin: '200px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [loadOlder, hasLoaded, runs.length]);

  const toggle = (runId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
        return next;
      }
      next.add(runId);
      // Lazy-fetch the full record on first expand only — cache per
      // runId so collapse/re-expand never refetches.
      setDetails((d) => {
        if (d[runId]) return d; // already fetched (or in-flight)
        Apps.getRun(instanceId, runId)
          .then((data) => setDetails((dd) => ({ ...dd, [runId]: { loading: false, error: null, data } })))
          .catch((err) => setDetails((dd) => ({ ...dd, [runId]: { loading: false, error: err.message || 'Failed to load run', data: null } })));
        return { ...d, [runId]: { loading: true, error: null, data: null } };
      });
      return next;
    });
  };

  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        <div className="card-header app-logs-header">
          <h2>Agent activity</h2>
          <div className="app-logs-controls">
            {/* No pause/stop toggle here — new runs arrive ~hourly and
                land at the top of the list without clobbering anything
                expanded below, so the pause button has nothing to do.
                Just the polling indicator. */}
            <span className="app-logs-live" title="Polling every 10s">Live</span>
          </div>
        </div>

        {!hasLoaded ? (
          // Skeleton: 3 ghost rows shimmering while the first fetch is
          // in-flight. We don't know the real count yet — 3 is enough to
          // read as "list", small enough to avoid pretending we have
          // data. Once `hasLoaded` flips (success or error), this is
          // replaced by the real list / empty / error.
          <ul className="app-convo-list app-convo-skeleton" aria-hidden>
            {[0, 1, 2].map((i) => (
              <li key={i} className="app-convo-item app-convo-skel-item">
                <div className="app-convo-summary app-convo-skel-summary">
                  <div className="app-convo-time app-convo-skel-bar app-convo-skel-bar--time" />
                  <div className="app-convo-headline app-convo-skel-bar app-convo-skel-bar--headline" />
                  <div className="app-convo-meta app-convo-skel-bar app-convo-skel-bar--meta" />
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <p className="app-logs-error">⚠ {error}</p>
        ) : runs.length === 0 ? (
          <p className="apps-modal-helper">
            No agent runs recorded yet. Runs from agent-ops v0.1.22+ appear here.
          </p>
        ) : (
          <ul className="app-convo-list">
            {runs.map((r) => {
              const isOpen = expanded.has(r.runId);
              const badge = RUN_STATUS_BADGE[r.status] || { cls: 'app-convo-badge--muted', label: r.status || 'unknown' };
              const headline = r.taskName || firstLine(r.summary) || '(unnamed run)';
              const cost = formatRunCost(r.costUsdMicro);
              const detail = details[r.runId];
              return (
                <li key={r.runId} className={`app-convo-item ${isOpen ? 'is-open' : ''}`}>
                  <button type="button" className="app-convo-summary" onClick={() => toggle(r.runId)}>
                    <div className="app-convo-time" title={r.startedAt ? timeAgo(r.startedAt) : ''}>
                      {formatLocalTime(r.startedAt)}
                    </div>
                    <div className="app-convo-headline">{headline}</div>
                    <div className="app-convo-meta">
                      <span className={`app-convo-badge ${badge.cls}`}>{badge.label}</span>
                      <span>{r.numTurns ?? '?'} turn{r.numTurns === 1 ? '' : 's'}</span>
                      <span>{cost}</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="app-convo-body">
                      {detail?.loading && (
                        <div className="app-convo-section-hint">Loading run detail…</div>
                      )}
                      {detail?.error && (
                        <p className="app-logs-error">⚠ {detail.error}</p>
                      )}
                      {detail?.data && (
                        <>
                          {detail.data.systemPrompt && (
                            <div className="app-convo-section">
                              <div className="app-convo-section-label">Agent context</div>
                              {/* Plain-English clarifier: what users see is NOT
                                  the full system prompt the model received.
                                  Claude Code CLI auto-injects its own preamble
                                  (tool schemas, output format, safety rules)
                                  that's hardcoded in the `claude` binary and
                                  not reachable from us. This block is only
                                  the slice agent-ops prepends on top. */}
                              <div className="app-convo-section-hint">
                                What agent-ops prepended for this run. Claude Code CLI's built-in
                                preamble (tool schemas, output format, safety) is invisible to us.
                              </div>
                              <pre className="app-convo-text">{detail.data.systemPrompt}</pre>
                            </div>
                          )}
                          {detail.data.userPrompt && (
                            <div className="app-convo-section">
                              <div className="app-convo-section-label">User prompt</div>
                              <pre className="app-convo-text">{detail.data.userPrompt}</pre>
                            </div>
                          )}
                          {detail.data.result && (
                            <div className="app-convo-section">
                              <div className="app-convo-section-label">Claude's response</div>
                              <pre className="app-convo-text app-convo-result">{detail.data.result}</pre>
                            </div>
                          )}
                          {r.status !== 'completed' && (detail.data.error || r.error) && (
                            <div className="app-convo-section">
                              <div className="app-convo-section-label">Error</div>
                              <pre className="app-convo-text app-convo-error">{detail.data.error || r.error}</pre>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
            {/* Bottom sentinel — the IntersectionObserver watches this to
                auto-load the next older page as it scrolls into view. The
                footer doubles as the loading / end-of-history marker. */}
            <li ref={sentinelRef} className="app-convo-loader" aria-hidden>
              {loadingOlder
                ? 'Loading older runs…'
                : (hasMore ? '' : '— End of history —')}
            </li>
          </ul>
        )}
      </div>
    </div>
  );
};

// First non-empty line of a multi-line string, used as a headline
// fallback when a run has no taskName.
function firstLine(s) {
  if (!s) return '';
  const t = String(s).split('\n')[0].trim();
  return t.length > 120 ? t.slice(0, 117) + '…' : t;
}

// Format a run's cost from microdollars (costUsdMicro / 1e6). Sub-cent
// values get 4 decimals ($0.0042); cent-and-up get 2 ($1.23). Mirrors
// the old `total_cost_usd` rendering.
function formatRunCost(costUsdMicro) {
  if (costUsdMicro == null || !Number.isFinite(Number(costUsdMicro))) return '—';
  const usd = Number(costUsdMicro) / 1e6;
  return `$${usd < 0.01 ? usd.toFixed(4) : usd.toFixed(2)}`;
}

function formatLocalTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────

function formatBytes(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const v = n / Math.pow(1024, i);
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function safeCronstrue(expr) {
  try { return cronstrue.toString(expr, { use24HourTimeFormat: true }); }
  catch { return expr; }
}

/**
 * Split a backend log line into colourable fields for industrial-style
 * rendering. Each backend log line is shaped:
 *
 *   `<iso-ts>  <json-body>`         (typical: slog JSON output)
 *   `<iso-ts>  <plain-text>`        (rare: agent's stderr/stdout)
 *
 * Returns:
 *   {
 *     ts:       'YYYY-MM-DDThh:mm:ss.sssZ' | null
 *     level:    'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | null
 *     msg:      short slog `msg` field   | null
 *     body:     human-readable remainder | full line for non-JSON
 *     severity: 'info' | 'warn' | 'error'  ← drives the row colour
 *   }
 *
 * Severity precedence:
 *   1. JSON `level` field (most reliable)
 *   2. Keyword sniff on `line` / body — npm's `warn deprecated` shows up
 *      as level=INFO from claude-code's bash-tool wrapper, but it IS a
 *      warning to the human reader. We re-class it amber so the
 *      bootstrap install spam can be visually skimmed past.
 *   3. Default `info`
 */
function parseLogLine(raw) {
  const fallback = { ts: null, level: null, msg: null, body: raw, severity: 'info' };
  if (typeof raw !== 'string' || raw.length < 1) return fallback;
  const idx = raw.indexOf('  ');
  if (idx < 0) return fallback;
  const ts = raw.slice(0, idx);
  const rest = raw.slice(idx + 2).trim();
  if (!rest.startsWith('{')) {
    return { ...fallback, ts, body: rest };
  }
  let obj;
  try { obj = JSON.parse(rest); } catch { return { ...fallback, ts, body: rest }; }
  const level = (obj.level || '').toUpperCase() || null;
  const msg = obj.msg || null;
  // Render the "interesting" part. For bash-wrapped script output
  // (msg=bootstrap-script, stream=stderr|stdout, line=…) the `line`
  // field IS the user-facing payload; show it instead of the JSON
  // bookkeeping. Otherwise condense the remaining fields.
  let body = '';
  if (typeof obj.line === 'string') {
    body = obj.line;
    if (obj.stream && obj.stream !== 'stdout') {
      body = `[${obj.stream}] ${body}`;
    }
  } else {
    const ignored = new Set(['time', 'level', 'msg']);
    const fields = Object.entries(obj).filter(([k]) => !ignored.has(k));
    if (fields.length > 0) {
      body = fields.map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`).join(' ');
    }
  }
  let severity = 'info';
  if (level === 'ERROR' || level === 'FATAL') severity = 'error';
  else if (level === 'WARN' || level === 'WARNING') severity = 'warn';
  // Keyword sniff: npm's "warn deprecated" / "ERESOLVE" / "error:" all
  // land at INFO level from the bash tool but a human treats them as
  // warnings / errors. Cheap regex over the rendered body keeps the
  // visual signal accurate without depending on the source's level
  // field being correct.
  if (severity === 'info' && /\b(error|failed|fatal|eresolve)\b/i.test(body)) {
    severity = 'error';
  } else if (severity === 'info' && /\b(warn|warning|deprecated)\b/i.test(body)) {
    severity = 'warn';
  }
  return { ts, level, msg, body, severity };
}

export default AppDetailPage;

// Exposed for unit tests (mirrors PipelineDetailPage's
// __test_only_WarmPoolSection). The section is module-private; this lets a
// test mount + exercise its state machine without the full router/outlet
// context the page wrapper needs.
export { StaticIpSection as __test_only_StaticIpSection };
